// Types + pure helpers for the country-pack-driven Employee Profile form.
// The shape mirrors payroll.tax_rule_sets.identifier_schema.employee, surfaced by
// GET /api/v1/payroll/countries/{code}/employee-profile-schema. NOTHING here is
// Tanzania-specific — the pack drives labels, masks, visibility and validation, so
// adding a country is a pack + tests change, not a React change.

export type Sensitivity = 'PII_HIGH' | 'PII_MEDIUM' | 'PII_LOW' | string;

// Context a field's requiredWhen/visibleWhen predicates evaluate against — the
// other answers on the form (residency, chosen social-security scheme, HESLB flag).
export interface ProfileContext {
  residentialStatus?: string;
  citizenship?: string;
  employmentType?: string;
  socialSecurityScheme?: string;
  heslbBeneficiary?: boolean;
  [k: string]: string | number | boolean | undefined;
}

export interface VerificationSpec {
  capable?: boolean;
  authority?: string;
  default?: string;
}

export interface IdentifierField {
  key: string;              // injected from the map key
  label: string;
  authority?: string;
  order?: number;
  inputType?: 'masked_digits' | 'text';
  displayMask?: string;     // e.g. "###-###-###" — presentation only
  normalizedFormat?: 'digits' | 'raw';
  regex?: string;           // validates the NORMALIZED value
  minLength?: number;
  maxLength?: number;
  placeholder?: string;
  helpText?: string;
  required?: boolean;
  requiredWhen?: Record<string, string | number | boolean>;
  visibleWhen?: Record<string, string | number | boolean>;
  sensitivity?: Sensitivity;
  maskInLists?: boolean;
  // Where the value is stored on the PAYROLL snapshot. Absent → the statutory_ids
  // map keyed by `key`; "national_id"/"passport_number" → a dedicated column.
  mapsTo?: string;
  // The employee-service (HR system-of-record) column this identity value is
  // written to. Present ⇒ the form writes it to employee-service (SoR) and payroll
  // receives it via events; absent ⇒ it stays a payroll-assignment field (e.g. HESLB).
  employeeField?: string;
  verification?: VerificationSpec;
}

export interface EmployeeProfileSchema {
  country: string;
  asOf: string;
  employee: Record<string, Omit<IdentifierField, 'key'>>;
}

// Ordered, key-injected field list for rendering.
export function fieldList(schema: EmployeeProfileSchema | null): IdentifierField[] {
  if (!schema?.employee) return [];
  return Object.entries(schema.employee)
    .map(([key, f]) => ({ ...f, key }))
    .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
}

// --- predicate evaluation --------------------------------------------------
// A when-clause is satisfied when every stated key matches the context. Absent
// context values never satisfy a clause (fail-closed for visibility/required).
function whenSatisfied(clause: Record<string, unknown> | undefined, ctx: ProfileContext): boolean {
  if (!clause) return true; // no clause → always
  return Object.entries(clause).every(([k, v]) => ctx[k] === v);
}

export function isVisible(field: IdentifierField, ctx: ProfileContext): boolean {
  return whenSatisfied(field.visibleWhen, ctx);
}

export function isRequired(field: IdentifierField, ctx: ProfileContext): boolean {
  if (field.requiredWhen) return whenSatisfied(field.requiredWhen, ctx);
  return !!field.required;
}

// --- normalization + masking ----------------------------------------------
export function normalizeValue(field: IdentifierField, raw: string): string {
  const s = raw ?? '';
  if (field.normalizedFormat === 'digits') {
    const digits = s.replace(/\D/g, '');
    return field.maxLength ? digits.slice(0, field.maxLength) : digits;
  }
  const trimmed = s.trim();
  return field.maxLength ? trimmed.slice(0, field.maxLength) : trimmed;
}

// Apply displayMask to a normalized value for on-screen presentation. Only the
// '#' placeholders consume value characters; literals are inserted between them.
export function applyMask(field: IdentifierField, normalized: string): string {
  const mask = field.displayMask;
  if (!mask || !normalized) return normalized;
  let out = '';
  let i = 0;
  for (const ch of mask) {
    if (i >= normalized.length) break;
    if (ch === '#') { out += normalized[i++]; } else { out += ch; }
  }
  // Any overflow beyond the mask is appended so nothing typed is silently dropped.
  if (i < normalized.length) out += normalized.slice(i);
  return out;
}

// --- validation ------------------------------------------------------------
// Returns a human error string, or null when valid. Validates the NORMALIZED
// value (never the masked display). `label` interpolation is left to the caller/i18n.
export function validateField(field: IdentifierField, raw: string, ctx: ProfileContext): string | null {
  const value = normalizeValue(field, raw);
  const required = isRequired(field, ctx);
  if (!value) return required ? `${field.label} is required` : null;
  if (field.minLength && value.length < field.minLength) {
    return `${field.label} must be at least ${field.minLength} characters`;
  }
  if (field.maxLength && value.length > field.maxLength) {
    return `${field.label} must be at most ${field.maxLength} characters`;
  }
  if (field.regex) {
    try {
      if (!new RegExp(field.regex).test(value)) return `${field.label} format is invalid`;
    } catch {
      /* a malformed pack regex must never hard-fail the form */
    }
  }
  return null;
}
